import { createContext, useContext, useMemo, type ReactNode } from "react";

export type Locale = "en" | "es";

export const DEFAULT_LOCALE: Locale = "en";
export const SITE_URL = "https://neurowellnesstms.com";

interface LocaleContextValue {
  locale: Locale;
  isEs: boolean;
  /** Prepend a locale prefix to a path. withLocale("/saint","es") → "/es/saint" */
  withLocale: (path: string, locale?: Locale) => string;
  /** Strip the /es prefix. stripLocale("/es/saint") → "/saint" */
  stripLocale: (path: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** Detect the locale from a pathname. */
export function detectLocale(pathname: string): Locale {
  if (pathname === "/es" || pathname.startsWith("/es/")) return "es";
  return "en";
}

/** Strip a leading /es or /es/ from a path. */
export function stripLocale(path: string): string {
  if (path === "/es") return "/";
  if (path.startsWith("/es/")) return path.slice(3);
  return path;
}

/** Prepend a locale prefix to a path. */
export function withLocale(
  path: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (locale === "en") return path;
  if (path === "/") return "/es";
  return `/es${path}`;
}

/** Build hreflang alternate <link> objects for a canonical path (no locale prefix). */
export function buildHreflang(canonicalPath: string) {
  const en = `${SITE_URL}${withLocale(canonicalPath, "en")}`;
  const es = `${SITE_URL}${withLocale(canonicalPath, "es")}`;
  return [
    { rel: "alternate", hrefLang: "en", href: en },
    { rel: "alternate", hrefLang: "es", href: es },
    { rel: "alternate", hrefLang: "x-default", href: en },
  ];
}

export function LocaleProvider({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      isEs: locale === "es",
      withLocale: (path: string, l: Locale = locale) => withLocale(path, l),
      stripLocale,
    }),
    [locale],
  );
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    return { locale: "en", isEs: false, withLocale, stripLocale };
  }
  return ctx;
}
